<template>
    <flex-element :element-type="elementType" class="f-flex f-flex-col">
        <div class="f-relative f-h-1/1">
            <o-container
                v-if="dataSourceNew"
                v-bind="element(1)"
                @element-updated="elementUpdated"
            >
                <transition-group
                    tag="div"
                    name="transition-slide"
                    enter-active-class="fadeInCarousel"
                    leave-active-class="fadeOutCarousel"
                >
                    <!-- ---------- slides: ---------- -->
                    <article
                        v-for="(item, index) in slides"
                        v-show="showSlide(index)"
                        :key="item.id"
                        class="f-flex f-flex-row tab:f-flex-col mob:f-flex-col"
                    >
                        <section class="f-relative f-w-1/2 tab:f-w-1/1 mob:f-w-1/1 f-min-h-24">
                            <div class="f-relative fu-placement-fill f-z-1">
                                <div data-image-wrapper class="f-overflow-hidden f-min-h-1/1 fu-placement-fill f--z-1">
                                    <img
                                        class="f-w-1/1 f-object-cover f-w-1/1 f-h-1/1"
                                        :src="item.imageUrl"
                                        :alt="item.imageAlt"
                                        data-image
                                    >
                                </div>
                            </div>

                            <div class="fc-invert-logo fu-placement-fill f-z-10 f-flex f-justify-center f-items-center">
                                <div data-image-wrapper class=" f-w-1/2 tab:f-w-1/3 lap:f-w-1/3 f-max-w-16">
                                    <img
                                        class="f-w-1/1 f-object-contain"
                                        :src="item.imageSmallUrl"
                                        :alt="item.imageSmallAlt"
                                        data-image
                                    >
                                </div>
                            </div>
                        </section>

                        <section class="f-flex f-flex-col f-justify-center f-w-1/2 tab:f-w-1/1 mob:f-w-1/1 tab:f-min-h-0 mob:f-min-h-0 f-px-4 f-py-3 mob:f-px-3 mob:f-py-2">
                            <div class="f-font-paragraph f-pb-2 f-text-bc-20 f-break-words mob:f-mt-1" v-html="item.text" />

                            <a
                                class="f-font-paragraph f-text-bc-19 hover:f-text-bc-11"
                                :href="item.linkUrl"
                                :target="item.linkTarget"
                            >
                                {{ item.linkText }}
                            </a>
                        </section>
                    </article>
                </transition-group>
            </o-container>

            <!-- arrows: -->
            <a-icon
                v-if="dataSourceNew"
                icon="chevron-left"
                set="fal"
                aria-label="previous slide"
                v-bind="element(2)"
                @element-updated="elementUpdated"
                @click="onArrowBackClick"
            />

            <a-icon
                v-if="dataSourceNew"
                icon="chevron-right"
                set="fal"
                aria-label="next slide"
                v-bind="element(3)"
                @element-updated="elementUpdated"
                @click="onArrowNextClick"
            />
        </div>

        <!-- bullets: -->
        <o-container
            v-if="dataSourceNew"
            tag="nav"
            class="tab:f-hidden mob:f-hidden"
            v-bind="element(4)"
            @element-updated="elementUpdated"
        >
            <div
                v-for="(item, index) in slides"
                :key="item.id"
                class="f-relative f-cursor-pointer f-mx-0.25 f-max-w-16 f-p-3 tab:f-p-1.5 f-w-1/1 fu-grayscale-100"
                :class="{ 'f-opacity-50': (index !== currentSlideIndex) }"
                @click="onBulletClick(index)"
            >
                <div data-image-wrapper class="f-overflow-hidden f-min-h-1/1">
                    <img
                        data-image
                        :src="item.imageSmallUrl"
                        :alt="item.imageSmallAlt"
                        class="f-w-1/1 f-object-cover"
                        aria-label="select slide"
                    >
                </div>
            </div>
        </o-container>
    </flex-element>
</template>

<script>
import baseOrganism from '@/mixins/base-organism/base-organism-dumb';
import carouselPromoQuery from '@/graphql/queries/CarouselPromo.gql';

export default {
    mixins: [baseOrganism],

    apollo: {
        itemData: {
            query: carouselPromoQuery,

            variables() {
                return {
                    dataSourceId: this.dataSourceNew,
                };
            },

            update(data) {
                if (!data || !data.item) return [];
                return data.item;
            },
        },
    },

    props: {
        dataSource: {
            type: String,
            default: '',
        },

        activeIndex: {
            type: Number,
            default: -1,
        },

        tag: {
            type: String,
            default: 'carousel-promo',
        },
    },

    data() {
        return {
            slideDirection: 'next',
            timer: null,
            currentSlideIndex: 0,
            slides: [],
        };
    },

    computed: {
        dataSourceNew() {
            return this.getElementData('carousel-promo-data-source') || '';
        },
    },

    watch: {
        // watch itemData and translate graphQL data
        itemData() {
            if (this.itemData.length < 1) {
                this.slides = [];
                return;
            }

            // carousel items
            const tempSlides = [];
            const slideData = this.itemData.carouselItems.targetItems;

            for (let i = 0; i < slideData.length; i++) {
                const slideItem = {
                    id: i + 1,
                    text: slideData[i].description.rendered,
                    linkText: slideData[i].link.text,
                    linkUrl: slideData[i].link.url,
                    linkTarget: slideData[i].link.target,
                    imageAlt: slideData[i].image.alt,
                    imageUrl: slideData[i].image.src,
                    imageSmallAlt: slideData[i].imageSmall.alt,
                    imageSmallUrl: slideData[i].imageSmall.src,

                };

                tempSlides.push(slideItem);
            }

            this.slides = tempSlides;
        },
    },

    created() {
        this.atomicType = 'organism';
        this.templateType = 'flex-carousel';
        this.setDataSource();
    },

    methods: {
        showSlide(index) {
            return index === this.currentSlideIndex;
        },

        toPrevSlide() {
            this.slideDirection = 'prev';

            // if current slide is at the end of the list, go to first slide:
            if (this.currentSlideIndex === 0) {
                this.currentSlideIndex = this.slides.length - 1;
            } else {
                this.currentSlideIndex--;
            }
        },

        toNextSlide() {
            this.slideDirection = 'next';

            // if current slide is at the end of the list, go to first slide:
            if (this.currentSlideIndex === this.slides.length - 1) {
                this.currentSlideIndex = 0;
            } else {
                this.currentSlideIndex++;
            }
        },

        onArrowBackClick() {
            this.toPrevSlide();
        },

        onArrowNextClick() {
            this.toNextSlide();
        },

        onBulletClick(clickedIndex) {
            // early return:
            if (clickedIndex === this.currentSlideIndex) {
                return;
            }

            if (clickedIndex > this.currentSlideIndex) {
                this.slideDirection = 'next';
            } else if (clickedIndex < this.currentSlideIndex) {
                this.slideDirection = 'prev';
            }
            this.currentSlideIndex = clickedIndex;
        },

        setDataSource() {
            if (this.dataSource && !this.dataSourceNew) {
                this.setElementData('carousel-promo-data-source', this.dataSource, { triggerChangeDetection: false });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.fc-invert-logo {
    img {
        -webkit-filter: brightness(0) invert(1);
        filter: brightness(0) invert(1);
    }
}

.fadeInCarousel {
    animation: fadeInCarousel 1s forwards;
}

@keyframes fadeInCarousel {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.fadeOutCarousel {
    animation: fadeOutCarousel 1s forwards;
    position: absolute !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
}

@keyframes fadeOutCarousel {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}

@media (print), (prefers-reduced-motion: reduce) {
    .animated-promo {
        animation-duration: 1ms !important;
        transition-duration: 1ms !important;
        animation-iteration-count: 1 !important;
    }
}
</style>
